import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import AddToLibraryButton from "../../../../../../components/ui/add-to-library-button/AddToLibraryButton";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import { ButtonGroup, TextInput as Text } from "@caelor/ui";
import IconPicker from "../../../../../../components/ui/icon-picker/IconPicker";
import LinkPicker from "../../../../../../components/ui/link-picker/LinkPicker";
import { buttonLibraryService } from "@Shared/Service/ButtonLibraryService";
import { isEqual } from "lodash";
import { useState } from "react";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
  OptionInput,
} from "../../styles";
import {
  BUTTON_PROP,
  BUTTON_TYPE,
  DEFAULT_HOVER,
  DEFAULT_WIDTH,
  LIBRARY_SERVICE_ACTION,
} from "./defaults";
import {
  ALIGNMENT_OPTIONS,
  BOOLEAN_OPTIONS,
  CORNERS_OPTIONS,
  ICON_POSITION_OPTIONS,
  SIZE_OPTIONS,
  STYLE_OPTIONS,
  INTERACTION_OPTIONS,
} from "./options";
import { buttonStores } from "../../../../../../stores/export-stores";
import { MACRO_KEY } from "../../../../../defaults";
import { BUTTON_STYLE } from "./options";
import {
  DEFAULT_WIDTH as DEFAULT_WIDTH_ICON,
  DEFAULT_WIDTH_TEXT,
  DEFAULT_WIDTH_TEXT_AND_ICON,
  MAX_WIDTH,
} from "./defaults";
const ButtonSettings = ({ editorMacro }) => {
  const { t } = useTranslation();

  const [isRequestingToLibrary, setIsRequestingToLibrary] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const lastMeasuredWidth = useRef(null);

  const { libraryButtons, setLibraryButtons, hasStyleguidePermission } =
    useOpticsStore((state) => ({
      libraryButtons: state.libraryButtons,
      setLibraryButtons: state.setLibraryButtons,
      hasStyleguidePermission: state.hasStyleguidePermission,
    }));

  const { data, setData } = buttonStores[editorMacro]((state) => ({
    data: state.data,
    setData: state.setData,
  }));

  const selectedLibraryButton = libraryButtons.find(
    (btn) => btn.id === data.id
  );

  const hasIcon = [BUTTON_TYPE.ICON, BUTTON_TYPE.TEXT_AND_ICON].includes(
    data.type
  );
  const hasIconPosition = data.type !== BUTTON_TYPE.ICON;

  const currentButtonStyle = useMemo(() => {
    return data.styleVariant || BUTTON_STYLE.OUTLINE;
  }, [data.styleVariant]);

  const getMinWidth = () => {
    if (data[BUTTON_PROP.TYPE] === BUTTON_TYPE.TEXT_AND_ICON)
      return DEFAULT_WIDTH_TEXT_AND_ICON;
    if (data[BUTTON_PROP.TYPE] === BUTTON_TYPE.TEXT) return DEFAULT_WIDTH_TEXT;
    return DEFAULT_WIDTH_ICON;
  };

  const measureButtonNaturalWidth = () => {
    const btn = document.querySelector(".macrosuite-button");
    if (!btn) return DEFAULT_WIDTH;

    const originalWidth = btn.style.width;
    btn.style.width = "auto";
    const naturalWidth = btn.offsetWidth;
    btn.style.width = originalWidth;

    return naturalWidth;
  };

  const updateButtonWidth = () => {
    requestAnimationFrame(() => {
      const naturalWidth = measureButtonNaturalWidth();
      const currentWidth = data[BUTTON_PROP.WIDTH];

      if (!currentWidth || lastMeasuredWidth.current !== currentWidth) {
        const finalWidth = naturalWidth;
        lastMeasuredWidth.current = finalWidth;
        setInputValue(finalWidth.toString());
        setData({ [BUTTON_PROP.WIDTH]: finalWidth });
      }
    });
  };

  const handleWidthChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleAccentColorChange = ({ accent, text }) => {
    setData({
      [BUTTON_PROP.ACCENT_COLOR]: text,
      [BUTTON_PROP.LABEL_COLOR]: text,
      [BUTTON_PROP.BACKGROUND_COLOR]: accent,
      [BUTTON_PROP.BORDER]: accent,
    });
  };

  const handleButtonLibraryRequest = async (fnName) => {
    try {
      setIsRequestingToLibrary(true);

      const response = await buttonLibraryService[fnName](data, "macro");
      if (response?.data?.btnId) {
        setData({ id: response.data.btnId });
        setLibraryButtons([
          ...libraryButtons,
          { ...data, id: response.data.btnId },
        ]);
      } else {
        setLibraryButtons(
          libraryButtons.map((btn) => (btn.id === data.id ? { ...data } : btn))
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsRequestingToLibrary(false);
    }
  };

  useEffect(() => {
    const minWidth = getMinWidth();
    const maxWidth = MAX_WIDTH;
    const parsed = parseFloat(inputValue);

    if (isNaN(parsed)) return;

    const applyValidation = () => {
      let clamped = parsed;
      if (parsed < minWidth) clamped = minWidth;
      else if (parsed > maxWidth) clamped = maxWidth;

      if (clamped !== parsed) {
        setInputValue(clamped.toString());
      }

      if (clamped !== data[BUTTON_PROP.WIDTH]) {
        setData({ [BUTTON_PROP.WIDTH]: clamped });
      }
    };

    if (inputValue.length === 2) {
      const timer = setTimeout(() => {
        applyValidation();
      }, 500);

      return () => clearTimeout(timer);
    }

    if (inputValue.length >= 3) {
      applyValidation();
    }
  }, [inputValue]);

  useEffect(() => {
    if (
      data[BUTTON_PROP.WIDTH] === undefined ||
      data[BUTTON_PROP.WIDTH] === null
    ) {
      updateButtonWidth();
    } else {
      setInputValue(data[BUTTON_PROP.WIDTH].toString());
    }
  }, [data.id]);

  return (
    <>
      {editorMacro === MACRO_KEY.BUTTON && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("alignment")}</SettingsOptionHeading>
          <OptionContainer>
            <OptionLabel>{t("align")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={ALIGNMENT_OPTIONS}
                value={data[BUTTON_PROP.ALIGN]}
                onClick={(value) => setData({ [BUTTON_PROP.ALIGN]: value })}
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        </SettingsOptionContainer>
      )}
      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("flexible-display-items")}
        </SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("icon")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={
                data[BUTTON_PROP.TYPE] === BUTTON_TYPE.ICON ||
                data[BUTTON_PROP.TYPE] === BUTTON_TYPE.TEXT_AND_ICON
              }
              onClick={(isOn) => {
                const type = isOn
                  ? data[BUTTON_PROP.TYPE] === BUTTON_TYPE.TEXT
                    ? BUTTON_TYPE.TEXT_AND_ICON
                    : BUTTON_TYPE.ICON
                  : BUTTON_TYPE.TEXT;

                setData({ [BUTTON_PROP.TYPE]: type });
              }}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("label")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={
                data[BUTTON_PROP.TYPE] === BUTTON_TYPE.TEXT ||
                data[BUTTON_PROP.TYPE] === BUTTON_TYPE.TEXT_AND_ICON
              }
              onClick={(isOn) => {
                const type = isOn
                  ? data[BUTTON_PROP.TYPE] === BUTTON_TYPE.ICON
                    ? BUTTON_TYPE.TEXT_AND_ICON
                    : BUTTON_TYPE.TEXT
                  : BUTTON_TYPE.ICON;

                setData({ [BUTTON_PROP.TYPE]: type });
              }}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("link")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel alignToTop>{t("link-to")}</OptionLabel>
          <OptionContent>
            <LinkPicker
              value={data[BUTTON_PROP.URL]}
              onChange={(value) => setData({ [BUTTON_PROP.URL]: value })}
            />
          </OptionContent>
        </OptionContainer>
        {data[BUTTON_PROP.URL]?.source !== "email" && (
          <OptionContainer>
            <OptionLabel>{t("new-tab")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={data[BUTTON_PROP.NEW_TAB]}
                onClick={(value) => setData({ [BUTTON_PROP.NEW_TAB]: value })}
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("content")}</SettingsOptionHeading>
        {data[BUTTON_PROP.TYPE] !== BUTTON_TYPE.ICON && (
          <OptionContainer>
            <OptionLabel>{t("label")}</OptionLabel>
            <OptionContent>
              <Text
                placeholder={t("add")}
                value={data[BUTTON_PROP.LABEL]}
                onChange={(value) => setData({ [BUTTON_PROP.LABEL]: value })}
              />
            </OptionContent>
          </OptionContainer>
        )}
        {hasIcon && (
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <IconPicker
                defaultTab={
                  typeof data[BUTTON_PROP.ICON] === "object"
                    ? t("emojis")
                    : t("icons")
                }
                tabs={[t("icons"), t("emojis"), t("logos")]}
                icon={data[BUTTON_PROP.ICON]}
                onChange={({ value }) => setData({ [BUTTON_PROP.ICON]: value })}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("style")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={STYLE_OPTIONS}
              value={currentButtonStyle}
              onClick={(value) =>
                setData({ [BUTTON_PROP.STYLE_VARIANT]: value })
              }
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("corners")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={CORNERS_OPTIONS}
              value={data[BUTTON_PROP.CORNERS]}
              options={{ input: true }}
              onClick={(value) => setData({ [BUTTON_PROP.CORNERS]: value })}
              onChange={({ target: { value } }) => {
                if (value.length < 4) setData({ [BUTTON_PROP.CORNERS]: value });
              }}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("size")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={SIZE_OPTIONS}
              value={data[BUTTON_PROP.SIZE]}
              onClick={(value) => setData({ [BUTTON_PROP.SIZE]: value })}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel>{t("width")}</OptionLabel>
          <OptionContent>
            <OptionInput
              value={inputValue}
              onChange={handleWidthChange}
              placeholder={measureButtonNaturalWidth() || getMinWidth()}
              min={getMinWidth()}
              max={600}
              step={1}
            />
          </OptionContent>
        </OptionContainer>
        {hasIcon && hasIconPosition && (
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={ICON_POSITION_OPTIONS}
                value={data[BUTTON_PROP.ICON_POSITION]}
                onClick={(value) =>
                  setData({ [BUTTON_PROP.ICON_POSITION]: value })
                }
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
        <OptionContainer>
          <OptionLabel alignToTop>{t("colors")}</OptionLabel>
          <OptionContent>
            <ColorPicker
              value={{
                accent: data[BUTTON_PROP.BACKGROUND_COLOR],
                text: data[BUTTON_PROP.LABEL_COLOR],
              }}
              onChange={(clr) => handleAccentColorChange(clr)}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("interaction")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("hover")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={INTERACTION_OPTIONS}
              value={
                data[BUTTON_PROP.HOVER] !== undefined
                  ? data[BUTTON_PROP.HOVER]
                  : DEFAULT_HOVER
              }
              onClick={(value) => setData({ [BUTTON_PROP.HOVER]: value })}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
      {hasStyleguidePermission && (
        <SettingsOptionContainer style={{ border: "none", marginTop: "auto" }}>
          <OptionContainer>
            <AddToLibraryButton
              onClick={(action) =>
                handleButtonLibraryRequest(LIBRARY_SERVICE_ACTION[action])
              }
              isSaved={!!selectedLibraryButton}
              isLoading={isRequestingToLibrary}
              hasChanges={
                selectedLibraryButton
                  ? !isEqual(selectedLibraryButton, data)
                  : false
              }
              macroName="button"
            />
          </OptionContainer>
        </SettingsOptionContainer>
      )}
    </>
  );
};

export default ButtonSettings;
